import { useState, lazy, Suspense, useRef, useMemo } from "react";
import Icon from "@mdi/react";
import { mdiFloppy, mdiPen } from "@mdi/js";
import { Link, useRouter, useTitle } from "easy-page-router/react";

import { useHeaders, useSSRHook } from "../../ssrh/client";
import { useUser } from "../User";
import { Header, Loading, Remove, Separator } from "../components";
import { api } from "../services/api";


import { DownloadItem, Page, PageMeta } from "../../types";
import { handleContentFiles } from "../services/editor";
import Footer from "../components/Footer";
import { htmlToText } from "html-to-text";
import { DownloadAdmin, DownloadList } from "../components/Download";


const Editor = lazy(() => import("../components/Editor"));

export default function Page({ url }: { url: string }) {
    const { push } = useRouter();
    const user = useUser();

    const [page, error, isLoading, reload] = useSSRHook<Page & PageMeta>(`/api/page?url=${encodeURIComponent(url)}`);

    const [edit, setEdit] = useState<null | Page>(null);
    const [message, setMessage] = useState("");

    const refEditor = useRef<HTMLDivElement>(null);

    const h1 = page?.title ?? "TSO Krkonoše";

    const title = h1 + " | TSO Krkonoše";
    const description = useMemo(() => {
        const description = htmlToText(page?.content ?? "", { limits: { maxInputLength: 2000 } }).slice(0, 150);
        if (!description) return "Stránka technického svazu obcí Krkonoše";
        return description;
    }, [page]);

    useTitle(title);
    useHeaders({
        title,
        description,
        image: window.location.origin + "/logo.png",
        canonical: window.location.origin + window.location.pathname,
    });

    async function handleSave() {
        if (!page) return;
        if (!edit) return;
        if (!refEditor.current) {
            setMessage("Problém s editorem");
            return;
        }

        const { content, files } = await handleContentFiles(edit, refEditor.current);

        const [respond] = await api.updatePage({
            _id: edit._id,
            title: edit.title,
            content,
            files,
            downloads: page.downloads,
        });

        if (!respond) {
            setMessage("Ukládání změn selhalo");
            return;
        }

        reload();
        setMessage("Stránka úspěšně uložena");
        setEdit(null);
        push(`/o-nas/${respond.url}`);
    }

    async function handleRemove() {
        if (!page) return;
        await api.removePage({ pageId: page._id });
        push("/o-nas");
    }

    async function handleChangeDownloads(downloads: DownloadItem[]) {
        if (!page) return false;

        const [, error] = await api.updatePage({
            ...page,
            downloads,
        });
        reload();
        return error === null;
    }

    return <>
        <main>
            <Header background="pens" title={h1}>
                <Link to="/o-nas">O nás</Link>
                <span className="space">-</span>
                {h1}
            </Header>
            <div className="content">
                <section>
                    <div className="page">
                        {isLoading && <Loading />}
                        {error && <p className="alert">Stránka se bohužel nepodařila načíst 🙁</p>}

                        {page && <>
                            {user.isAdmin && (
                                <div className="admin">
                                    {!edit && <button onClick={() => setEdit(page)}>
                                        <Icon path={mdiPen} size={1} />{" "}
                                        Upravit stránku
                                    </button>}
                                    {!edit && <Remove onRemove={handleRemove}>Smazat stránku</Remove>}
                                    {edit && <button onClick={handleSave}>
                                        <Icon path={mdiFloppy} size={1} />{" "}
                                        Uložit změny
                                    </button>}
                                </div>
                            )}

                            {!edit && <>
                                <h1>{page.title}</h1>
                                <Separator />
                                {user.isAdmin && message && <p className="alert">{message}</p>}
                                <div className="html-content ck-content" dangerouslySetInnerHTML={{
                                    __html: page.content || "<p>Tato stránka je prázdná</p>",
                                }} />
                                <DownloadList downloads={page.downloads || []} />
                            </>}
                            {user.isAdmin && edit && <>
                                <input
                                    className="h1"
                                    type="text"
                                    value={edit.title}
                                    onChange={(e) => setEdit({ ...edit, title: e.target.value })}
                                />
                                <Separator />
                                {message && <p className="alert">{message}</p>}
                                <div ref={refEditor}>
                                    <Suspense fallback={<Loading />}>
                                        <Editor
                                            value={edit.content}
                                            onChange={(value) => setEdit({ ...edit, content: value })}
                                        />
                                    </Suspense>
                                </div>

                                <DownloadAdmin downloads={page.downloads || []} onChange={handleChangeDownloads} />
                            </>}
                        </>}
                    </div>
                </section>
            </div>
        </main>
        <Footer />
    </>;
}